import { CREATOR_SHOP_SORT, LIMITED_PRODUCT_SORT, PRODUCT_LIST_SORT } from '../constant';
import { BestProductQueryParams } from '../validate/best_product';

export interface CateFilterOption {
  cate_root_id: number | null;
  cate_list_id?: number;
  cate_item_id?: number;
}

export interface PriceFilterOption {
  min_price?: number;
  max_price?: number;
}

/**
 * 상품 "크리에이터" 필터
 */
export interface CreatorFilterOption {
  creator_filter_ids?: number[];
}

/**
 * 상품 "스타일" 필터
 */
export interface StyleFilterOption {
  style_filter_ids?: number[];
}

/**
 * 상품필터 모달에 존재하는 상품 필터
 */
export interface ProductFilterOption extends PriceFilterOption, CreatorFilterOption, StyleFilterOption {}

export interface CrewFilterOption {
  crew_id?: number;
}

export interface ProductIncludeOption {
  /**
   * @default false
   * @description **비공개 상품**의 포함 여부를 제어합니다.
   * - `true` 비공개 상품 **포함**
   * - `false` 비공개 상품 **제외**
   */
  with_private?: boolean;

  /**
   * @default false
   * @description **독점 상품**의 포함 여부를 제어합니다.
   * - `true` **퍼블릭하지 않은** 페이지에도 보여질 수 있는 상품들을 가져옵니다.
   * - `false` 메인이나 검색 등 퍼블릭한 상품 목록에 보여질 수 있는 상품만 가져옵니다.
   */
  with_exclusive?: boolean;

  /**
   * @default false
   * @description **품절 상품**의 포함 여부를 제어합니다.
   * - `true` 품절 상품 **제외**
   * - `false` 품절 상품 **포함**
   */
  without_sold_out?: boolean;

  /**
   * TODO: @kjj
   * @description 타입 에러 발생 -> 원래 있었다가 사라진 타입으로 의미 불명
   * 경진님 부탁해요~!
   * */
  is_limited?: boolean;
}

export interface PaginationOption {
  limit: number;
  offset?: number;
}

export interface RequiredRetrieveOption {
  adult_check_needed: boolean;
}

/**
 * `products_dbt` 필터 옵션
 * 필터링 조건에 해당하는 프로퍼티만 기재합니다.
 */
export interface ProductsDbtFilterOption
  extends CateFilterOption,
    CrewFilterOption,
    ProductIncludeOption,
    CreatorFilterOption,
    StyleFilterOption {}
/**
 * `products_dbt` 조회 옵션
 * 정렬이나, 좋아요 등 필터 조건에 해당하지 않는 조회 옵션을 기재합니다.
 */
export interface ProductsDbtRetrieveOption extends PaginationOption, RequiredRetrieveOption {
  sort: keyof typeof PRODUCT_LIST_SORT;

  user_id: number | null;
}

// Shop
/**
 * "Shop > 상품 목록" 필터 옵션
 */
export interface ShopFilterOption
  extends CateFilterOption,
    ProductFilterOption,
    CrewFilterOption, // FIXME: @kjj
    Pick<ProductIncludeOption, 'without_sold_out' | 'is_limited'> {}
/**
 * "Shop > 상품 목록" 조회 옵션
 *
 * 정렬이나, 좋아요 등 필터 조건에 해당하지 않는 조회 옵션을 기재합니다.
 */
export interface ShopRetrieveOption extends ProductsDbtRetrieveOption, PaginationOption {
  include_ranking?: boolean;
}

// Plus Shop
/**
 * "Plus Shop > 상품 목록" 필터 옵션
 */
export interface PlusShopFilterOption
  extends CateFilterOption,
    ProductFilterOption,
    Required<CrewFilterOption>,
    Pick<ProductIncludeOption, 'without_sold_out' | 'is_limited'> {}
/**
 * "Shop > 상품 목록" 조회 옵션
 *
 * 정렬이나, 좋아요 등 필터 조건에 해당하지 않는 조회 옵션을 기재합니다.
 */
export interface PlusShopRetrieveOption extends ProductsDbtRetrieveOption, PaginationOption {
  include_ranking?: boolean;
}

// Creator Shop
export interface CreatorShopFilterOption
  extends CateFilterOption,
    Pick<ProductIncludeOption, 'without_sold_out'> {
  store_id: number;

  /**
   * store_ids 먼저 적용됩니다.
   *
   * @deprecated do not use
   * 브랜드 커머스에서 하드코딩할 때 필요한데, 한달 후에 제거될 코드라 서비스 코드는 따로 수정하지 않았습니다.
   */
  store_ids?: number[];
}
export interface CreatorShopRetrieveOption extends RequiredRetrieveOption, PaginationOption {
  sort: keyof typeof CREATOR_SHOP_SORT;

  /**
   * @remarks
   * user_id: number | null 로 수정 필요
   */
  user_id?: number | null;
}

// Search
export interface SearchFilterOption {
  // 검색어
  search: string;

  // 스토어 내부 검색 시 필요
  store_id?: number;
}
export interface SearchRetrieveOption extends ProductsDbtRetrieveOption, PaginationOption {}

// user_liked
export interface UserLikedFilterOption {
  user_id: number;
}
export type UserLikedRetrieveOption = PaginationOption & RequiredRetrieveOption;

// by ids
export interface ByIdsFilterOption extends ProductIncludeOption {
  /**
   * - `products.id`의 배열입니다. `product_id` 아님에 주의해주세요.
   * - `products.id`는 integer 이지만, 사용 편의를 위해 `string[]`도 허용합니다.
   */
  ids: number[] | string[];
}

export interface ByIdFilterOption extends ProductIncludeOption {
  /**
   * - `products.id`의 아이디입니다. `product_id` 아님에 주의해주세요.
   * - `products.id`는 integer 이지만, 사용 편의를 위해 `string`도 허용합니다.
   */
  id: number | string;
}

export interface ByIdsRetrieveOption extends RequiredRetrieveOption {
  user_id: number | null;
  include_ranking?: boolean;
}

export interface ByStoresProductsIdsFilterOption extends UserLikedFilterOption {
  /**
   * - `products.id`의 배열입니다. `product_id` 아님에 주의해주세요.
   * - `products.id`는 integer 이지만, 사용 편의를 위해 `string[]`도 허용합니다.
   */
  stores_product_ids: number[] | string[];
}

// Limited Edition
export type LimitedEditionFilterOption = ProductsDbtFilterOption;
export interface LimitedEditionRetrieveOption extends PaginationOption, RequiredRetrieveOption {
  sort: keyof typeof LIMITED_PRODUCT_SORT;
  user_id: number | null;
  include_ranking?: boolean;
}

export enum ProductRetrievePlugin {
  // n 명이 보는 중
  watcher_count = 'watcher_count',

  // 랭킹
  ranking = 'ranking',

  // 급상승
  trending = 'trending',
}

export type BestProductOption = RequiredRetrieveOption &
  BestProductQueryParams & {
    user_id: number | null;
    current_date: Date;
    plugins?: ProductRetrievePlugin[];
  };
